import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import { constants } from "../../constants/constants";
import { ADMIN_OPEN_FORUM_EVENT } from "../../routes/index.routes";
import { OPEN_FORUM_API_ROUTES } from "../../routes/api.routes";
import useFormData from "../useFormData";



const { API_URL } = constants();





/**
 * Custom hook to fetch applicants data for the open forum event.
 *
 * This hook uses the `useQuery` hook from React Query to fetch paginated data
 * from the open forum RSVP API. It handles pagination internally and aggregates
 * all the data into a single array.
 *
 * @returns {Object} - The result of the `useQuery` hook, including the aggregated data.
 */
export const useGetApplicantsData = () => {
  return useQuery({
    queryKey: [ADMIN_OPEN_FORUM_EVENT],
    queryFn: async () => {
      let allData = [];
      let page = 1;
      let totalPages = 1;
      let firstResponse = null;

      do {
        const response = await axios.get(
          `${API_URL}${OPEN_FORUM_API_ROUTES.RSVP}?page=${page}&limit=50`
        );

        if (!firstResponse) {
          firstResponse = response.data;
        }

        allData = [...allData, ...response.data.data];
        totalPages = response.data.pages;
        page++;
      } while (page <= totalPages);

      return { ...firstResponse, data: allData };
    },
  });
};

export const useExportToCSV = () => {
  return useQuery({
    queryKey: [OPEN_FORUM_API_ROUTES.EXPORT_CSV],
    queryFn: async () => {
      const { data } = await axios.get(
        `${API_URL}${OPEN_FORUM_API_ROUTES.EXPORT_CSV}`
      );
      return data;
    },
  });
};

export const useGetOpenForumRSVPById = (id) => {
  return useQuery({
    queryKey: [id],
    queryFn: async () => {
      const { data } = await axios.get(
        `${API_URL}${OPEN_FORUM_API_ROUTES.RSVP}/${id}`
      );
      return data;
    },
  });
};

export const useOpenForumRSVP = (data, cv) => {
  const RSVPDetails = {
    existingObject: {
      ...data,
      currentDeviceUsed: data?.currentDeviceUsed?.value,
      gender: data?.gender?.value,
      ageRange: data?.ageRange?.value,
    },
    file: cv,
    key: "cv",
  };

  const formData = useFormData(RSVPDetails);

  return useMutation({
    mutationFn: async () => {
      return await axios.post(
        `${API_URL}${OPEN_FORUM_API_ROUTES.RSVP}`,
        formData
      );
    },
  });
};

export const useUpdateOpenForumRSVP = (data, id) => {
  return useMutation({
    mutationFn: async () => {
      return await axios.put(
        `${API_URL}${OPEN_FORUM_API_ROUTES.RSVP}/${id}`,
        data
      );
    },
  });
};


export const useUpdateOpenForumRSVPAttendance = (id, data) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async () => {
      return await axios.put(
        `${API_URL}${OPEN_FORUM_API_ROUTES.MARK_ATTENDANCE}/${id}`,
        data
      );
    },
    onSuccess: () => {
      // Invalidate the RSVP data query to update the data
      queryClient.invalidateQueries(ADMIN_OPEN_FORUM_EVENT)
    },
  });
};

export const useDeleteOpenForumRSVP = (id) => {
  return useMutation({
    mutationFn: async () => {
      return await axios.delete(
        `${API_URL}${OPEN_FORUM_API_ROUTES.RSVP}/${id}`
      );
    },
  });
};
